<template>
  <!--
  mobile-breakpoint="0" prevents the table to
  switch to a mobile optimized list on small devices
  -->
  <v-data-table
    dense
    :loading="loading"
    :loading-text="$t('wgu-attributetable.loadingText')"
    :headers="disasterHeaders"
    :items="disastersList"
    mobile-breakpoint="0"
    :page.sync="page"
    :footer-props="{
        'items-per-page-options': [rowsPerPage],
        'show-first-last-page': true
      }"
    @click:row="onRowClick"
    single-select
    :value="selectedRow"
    :item-key="uniqueRecordKeyName"
    :items-per-page="rowsPerPage"
    :height="getTableHeight()"
  >
  </v-data-table>
</template>

<script>
import { Mapable } from '../../mixins/Mapable';
import LayerUtil from '../../util/Layer';
import { WguEventBus } from '../../WguEventBus';
import MapInteractionUtil from '../../util/MapInteraction';
import ViewAnimationUtil from '../../util/ViewAnimation';
import axios from '@/axios';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import VectorSource from 'ol/source/Vector';
import { mapState } from 'vuex';

export default {
  name: 'ks-disastertable',
  mixins: [Mapable],
  props: {
    /** The ID of the vector layer to display */
    layerId: { required: false, default: null },

    /** The name of the unique feature identifier */
    uniqueRecordKeyName: { type: String, required: false, default: 'id' },

    /**
     * How many rows a page of the table should have.
     * Should be manually adjusted with tableHeight.
     */
    rowsPerPage: { type: Number, required: true, default: 5 },

    /**
     * The height of the table in pixel.
     * Should be manually adjusted with rowsPerPage.
     */
    tableHeight: { type: Number, required: false, default: 224 },

    /** If map and table should be synced */
    syncTableMapSelection: { type: Boolean, required: false, default: true },

    /** A list of column names that should not be displayed. */
    forbiddenColumnNames: {
      type: Array,
      required: false,
      default: function () {
        return ['geometry', 'the_geom']
      }
    }
  },
  data () {
    return {
      records: [],
      features: [],
      selectedRow: [],
      layer: null,
      loading: true,
      page: 1,
      // rowsPerPage: 8,
      disastersList: [],
      disasterHeaders: [
        { text: 'ID', value: 'id' },
        { text: 'Address', value: 'address' },
        { text: 'Name', value: 'name' },
        { text: 'User Name', value: 'user_name' },
        { text: 'Crop Name', value: 'crop_name' },
        { text: 'State Name', value: 'state_name' },
        { text: 'Level Name', value: 'level_name' },
        { text: 'Type Name', value: 'type_name' },
        { text: 'Note', value: 'note' },
        { text: 'Create Time', value: 'create_time' }
      ],
      selectedVectorLayer: null
    }
  },
  created () {
    this.populateTable()
    if (this.syncTableMapSelection) {
      this.activateSelectRowOnMapClick();
    }
  },
  mounted () {
    this.getDisasters();
  },
  beforeDestroy () {
    if (this.layer && this.layer.getSource()) {
      // unregister event after table is closed
      this.layer.getSource().un('change', this.prepareTableDataAndColumns);
    }
  },
  computed: {
    ...mapState({
      selectedYear: state => state.selectedYear,
      selectedArea: state => state.selectedArea
    })
  },
  watch: {
    // Watch for changes in selectedYear
    selectedYear (newVal, oldVal) {
      if (newVal !== oldVal) {
        console.log('selectedYear changed', newVal, oldVal);
        this.getDisasters();
      }
    },
    // Watch for changes in selectedArea
    selectedArea (newVal, oldVal) {
      if (newVal !== oldVal) {
        console.log('selectedArea changed', newVal, oldVal);
        this.getDisasters();
      }
    },
    async layerId () {
      // this.populateTable()
      this.getDisasters();
    },
    features () {
      this.records = this.features.map(
        feature => {
          const record = feature.getProperties();
          // set feature id
          record[this.uniqueRecordKeyName] = feature.getId()
          return record;
        }
      );
    },
    records () {
      // set loading status depending if records are available
      if (this.records.length === 0) {
        this.loading = true;
      } else {
        this.loading = false;
        this.highlightInitialFeatureSelectionInTable();
      }
    }
  },
  methods: {
    // get disasters data from the server
    async getDisasters () {
      // Prepare the JSON data
      const jsonData = {
        year: this.selectedYear.value ? this.selectedYear.value : null,
        area: this.selectedArea && this.selectedArea.address && this.selectedArea.address.code ? this.selectedArea.address.code : null
      };
      try {
        // Send a POST request with JSON body
        const response = await axios.post('/?_c=api&_a=getDisasters', jsonData, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        const result = response.data.data.disastersList;
        console.log(result);
        this.disastersList = result;
      } catch (error) {
        console.error('Failed to fetch disasters:', error);
        // Handle error appropriately
      }

      console.log(this.selectedArea, this.selectedYear);
    },
    /**
     * Highlight row when feature is already selected
     * before table is opened.
     */
    highlightInitialFeatureSelectionInTable () {
      const correspondingInteraction = MapInteractionUtil.getSelectInteraction(this.map, this.layerId);

      if (!correspondingInteraction) {
        return;
      }

      const features = correspondingInteraction.getFeatures();
      if (!features || features.getLength() !== 1) {
        return;
      }

      const feature = features.getArray()[0];
      if (!feature) {
        return;
      }

      const fid = feature.getId();
      if (!fid) {
        return;
      }
      this.highlightRowFromSelectedFeature(fid);
    },

    /**
     * Set the table height depending on desktop
     * or mobile view.
     *
     * @returns {int} The height of the table.
     */
    getTableHeight () {
      if (this.$vuetify.breakpoint.xs) {
        // we do not want to set this property
        return undefined;
      } else {
        return this.tableHeight;
      }
    },

    /**
     * Activate behaviour that a selected feature on the
     * map will be selected in the AttributeTable as well.
     */
    activateSelectRowOnMapClick () {
      WguEventBus.$on('map-selectionchange',
        (lid, featureArray) => {
          if (lid !== this.layerId || featureArray.length !== 1) {
            return;
          }
          const fid = featureArray[0].getId();
          this.highlightRowFromSelectedFeature(fid);
        });
    },

    /**
     * Highlight row matching the feature ID
     * and set correct page of the table.
     *
     * @param {Number} fid The ID of the selected feature.
     */
    highlightRowFromSelectedFeature (fid) {
      if (!fid) {
        return;
      }
      const foundRecord = this.records.find(record => record.id === fid);
      if (!foundRecord) {
        return;
      }
      this.selectedRow = [foundRecord];

      const recIndex = this.records.indexOf(foundRecord);
      if (!recIndex) {
        return;
      }
      this.$nextTick(() => {
        // calculate page and set it
        this.page = Math.ceil((recIndex + 1) / this.rowsPerPage);
      })
    },

    /**
     * Handler for click on a row.
     *
     * It zooms to the clicked features.
     *
     * If the layer is 'selectable', the corresponding feature on the
     * map will be styled as selected.
     */
    onRowClick (record) {
      if (!this.syncTableMapSelection) {
        return;
      }

      // const rid = record[this.uniqueRecordKeyName];
      // if (!rid) {
      //   return;
      // }

      this.selectedRow = [record];

      const rname = `
                      ${record.address}
                      ${record.name}
                      ${record.crop_name}
                      ${record.level_name}
                      ${record.type_name}
                      ${record.note}
                      ${record.create_time}
                    `;
      const centerFeature = this.createFeatureFromCenter(record.center, rname);
      if (!centerFeature) {
        return;
      }

      const selectedVectorSource = new VectorSource({
        features: [centerFeature]
      });
      // find the layer with the earthquakes and update the source
      const layer = this.map.getLayers().getArray().find(layer => layer.get('lid') === 'activeParcelLayer');
      if (layer) {
        // const newSource = new VectorLayer({
        //   source: selectedVectorSource,
        //   style: new Style({ image: new Icon({ src: '/media/images/camera_s.png', scale: 0.8 }) })
        // });
        layer.setSource(selectedVectorSource);
      }

      // zoom to feature
      ViewAnimationUtil.to(this.map.getView(), centerFeature.getGeometry());

      const correspondingInteraction = MapInteractionUtil.getSelectInteraction(this.map, 'activeParcelLayer');

      // we can only select layers that have a select interaction
      if (!correspondingInteraction) {
        return;
      }

      // add to map selection
      correspondingInteraction.getFeatures().clear();
      correspondingInteraction.getFeatures().push(centerFeature);

      const overlayId = 'ks-popup-tooltip';
      // show popup and update content
      WguEventBus.$emit(overlayId + '-update-overlay', true, centerFeature.getGeometry().getCoordinates(), {
        feature: record,
        layer: this.layer,
        hoverAttribute: true
      });
    },

    /**
     * Load features from layer and display it in
     * in the table.
     */
    populateTable () {
      if (!this.map || !this.layerId) {
        return;
      }

      // reset table properties
      this.records = [];
      this.features = [];
      this.headers = [];
      this.selectedRow = [];
      this.page = 1;

      this.layer = LayerUtil.getLayerByLid(this.layerId, this.map);

      // load currently available features
      this.prepareTableDataAndColumns()

      // features can only be loaded if layer is visible
      // that's why we switch the layers on and retrieve them
      // once the features are available
      // https://github.com/openlayers/openlayers/blob/main/doc/faq.md#why-arent-there-any-features-in-my-source
      this.layer.getSource().on('change', this.prepareTableDataAndColumns);
      this.layer.setVisible(true);
    },

    /**
     * Loads the features from the layer source and
     * prepares the required columns for the table.
     */
    prepareTableDataAndColumns () {
      this.features = this.layer.getSource().getFeatures();
      this.applyColumnMapping();
    },

    /**
     * Read column mapping from layer property.
     * Otherwise we retrieve it from the feature
     * properties names.
     */
    applyColumnMapping () {
      if (!this.layer.getSource() ||
          !this.layer.getSource().getFeatures() ||
          !this.layer.getSource().getFeatures()[0]
      ) {
        return;
      }

      const headers = [];
      if (this.layer.get('columnMapping')) {
        for (const [propertyName, displayName] of Object.entries(this.layer.get('columnMapping'))) {
          headers.push({
            text: displayName,
            value: propertyName
          });
        }
      } else {
        // TODO: taking the first feature assumes that all features
        //       have the same structure
        const keys = this.layer.getSource().getFeatures()[0].getKeys();
        // remove keys that contain a geometry
        const filtered = keys.filter(
          key => (!this.forbiddenColumnNames.includes(key))
        );
        filtered.forEach(propertyName => {
          headers.push({
            text: propertyName,
            value: propertyName
          });
        });
      }
      this.headers = headers;
    },
    /**
     * Create a feature from the center coordinates.
     *
     * @param {Array} center The center coordinates.
     * @returns {Feature} The created feature.
     */
    createFeatureFromCenter (center, name) {
      // Create a point geometry from the center coordinates
      const point = new Point(center);

      // Create a feature with the point geometry
      const feature = new Feature(point);
      feature.set('name', name);

      return feature;
    },

    createPopupImage (item) {
      'use strict';
      let sHtml = '<div>';
      const imgWidth = '80px';
      for (let i = 1; i <= 6; i++) {
        if (item[`image_${i}`]) {
          sHtml += `<a style='border:1px solid #fff' href='javascript:void(0)' onclick='window.open("${item[`image_${i}`]}")'><img src='http://admin.kungsong.com/upload/resize_url.php?url=${item[`image_${i}`]}&w=80&h=80&cut=' width='${imgWidth}'></a>`;
        }
      }
      sHtml += '</div>'
      return sHtml;
    }
  }
}
</script>
<style scoped>
      .ol-popup {
        position: absolute;
        background-color: white;
        box-shadow: 0 1px 4px rgba(0,0,0,0.2);
        padding: 15px;
        border-radius: 10px;
        border: 1px solid #cccccc;
        bottom: 12px;
        left: -50px;
        min-width: 280px;
      }
      .ol-popup:after, .ol-popup:before {
        top: 100%;
        border: solid transparent;
        content: " ";
        height: 0;
        width: 0;
        position: absolute;
        pointer-events: none;
      }
      .ol-popup:after {
        border-top-color: white;
        border-width: 10px;
        left: 48px;
        margin-left: -10px;
      }
      .ol-popup:before {
        border-top-color: #cccccc;
        border-width: 11px;
        left: 48px;
        margin-left: -11px;
      }
      .ol-popup-closer {
        text-decoration: none;
        position: absolute;
        top: 2px;
        right: 8px;
      }
      .ol-popup-closer:after {
        content: "✖";
      }
    </style>
